/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보 결과 목록 상세보기
 * 버전 : 1.0
 * 파일명 : PromoResultDetail.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-17, 강동형 : 저작권 추가, 아이콘 변경 및 항목 추가, 태그 디자인 변경
 * 수정 : 2026-02-02, 학교 특징 추가
 *=================================================================*/

import React, { useEffect, useState } from 'react'
import { Typography, Box, IconButton, styled } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person';
import { PromoResultType } from 'src/pages/PromoResultPage';
import { promoResultType } from 'src/types/PromoType';
import { getDayOfWeek } from 'src/utils/DateUtils';

// 이미지 import
import PromoInterested from './PromoInterested';
import { Icon_Calendar, Icon_download_Gray, Icon_EdgeFoldPaper, Icon_Heart, Icon_X_Mark } from '../common/CommonIcons';
import { usePromoDialogInterest } from 'src/stores/PromoStore';
import { AppImage } from 'src/assets/images/AppImage';

interface Props{
    setTab : React.Dispatch<React.SetStateAction<PromoResultType>>;
    currentPromotion: promoResultType | undefined
}

const Container = styled(Box)`
  padding-top: 20px;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100vh;
  ;
`;

const Header = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center', // ✅ camelCase로 수정
    paddingBottom: '32px',
    fontSize:'18px',
    fontWeight:'bold'
  });

const CategoryHead = styled('span')({
    opacity: '0.5',
    flexShrink: 0,      /* ✅ 줄어들지 않게 */
    whiteSpace: 'nowrap' /* ✅ 줄바꿈도 방지(원하면) */
})

const TagContainer = styled('div')({
    display:'flex',
    gap: '10px',

    position:'absolute',
    top:'calc(100% + 6px)',
    fontSize:'12px',
    fontWeight:500
})

const ThicDivider = styled('div')({
    marginTop:'0px',
    marginBottom: '20px', 
    minHeight:'10px',
    backgroundColor:'#F5F5F5'
})

const PromoResultDetail = ({setTab, currentPromotion}:Props) => {
    const [parseType, setParseType] = useState('')
    const [school,setSchool] = useState<any>('')
    const [name,setName] = useState('')
    const [visited_At, setVisited_At] = useState('방문 기록 없음')
    const total_rows = usePromoDialogInterest((state)=>state.total_rows)

    useEffect(()=>{
        if(!currentPromotion) return

        if (currentPromotion.school?.school_type === '특성화고등학교') {
        setParseType('특성화고')
        } else if (currentPromotion.school?.school_type === '특수목적고등학교') {
        setParseType('특목고')
        } else if (currentPromotion.school?.school_type === '자율고등학교') {
        setParseType('자율고')
        }
        if(currentPromotion.school){
            setSchool(currentPromotion.school)
            setName(currentPromotion.school.school_name)
        }
        else if (currentPromotion.education){
            setSchool(currentPromotion.education)
            setName(currentPromotion.education.education_name)
        }
        setVisited_At(`${currentPromotion.visit_date}(${getDayOfWeek(currentPromotion.visit_date)})`)
    },[currentPromotion])

const hasAnyTag =
  !!parseType ||
  !!school.military_agreement ||
  !!school.rotc ||
  (school.special_major_list?.length ?? 0) > 0;

  return (
    <Container>
    <Header>
        홍보 결과
        <IconButton onClick={() => setTab('home')} sx={{position:'absolute',right:'12px'}}>
            <Icon_X_Mark/>
        </IconButton>
    </Header>
    {/* 학교 이름 */}
    <Box display="flex" justifyContent="space-between" position='relative' flexDirection='column' marginBottom={hasAnyTag ? '45px': '24px'} paddingX={'20px'}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', marginBottom: '4px', fontSize:'26px'}}>
        {name}
        </Typography>
        {/* 학교 타입 */}
        <TagContainer>
            {parseType &&
            <Typography variant="body2" sx={{ backgroundColor: '#FFFFFF', color: '#1E9468', border: '1px solid #1E9468', padding: '2px 4px', borderRadius: '4px', display: 'inline-block'}}>
                {parseType}
            </Typography>}
            {school.military_agreement &&
            <Typography variant="body2" sx={{ backgroundColor: '#FFFFFF', color: '#1E9468', border: '1px solid #1E9468', padding: '2px 4px', borderRadius: '4px', display: 'inline-block'}}>
                군협약
            </Typography>
            }
            {school.rotc &&
            <Typography variant="body2" sx={{ backgroundColor: '#FFFFFF', color: '#1E9468', border: '1px solid #1E9468', padding: '2px 4px', borderRadius: '4px', display: 'inline-block'}}>
                학군단
            </Typography>
            }
            {school.special_major_list?.length > 0 &&
            <Typography variant="body2" sx={{ backgroundColor: '#FFFFFF', color: '#1E9468', border: '1px solid #1E9468', padding: '2px 4px', borderRadius: '4px', display: 'inline-block'}}>
                우대학과
            </Typography>
            }
        </TagContainer>
    </Box>
    <div style={{padding:'0px 20px'}}>
        {/* 최근 방문 날짜 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
            <Icon_Calendar style={{width:'20px', height:'20px', marginRight:'8px'}} />
            {currentPromotion?.visit_date ? (
                <>
                    <CategoryHead>최근 방문 날짜 &nbsp;</CategoryHead>{visited_At}
                </>
                ) : (
                <> <CategoryHead>최근 방문 날짜 &nbsp;</CategoryHead>방문 기록 없음</>
            )}
        </Typography>
        {/* 전체 학생 수 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <PersonIcon fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <CategoryHead>전체 학생 &nbsp;</CategoryHead>
        {currentPromotion?.total_cnt}명
        </Typography>
        {/* 참여자 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <PersonIcon fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <CategoryHead>참여자 &nbsp;</CategoryHead>
        {currentPromotion?.attendee_cnt}명
        </Typography>
        {/* 관심표명자 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <Icon_Heart fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <CategoryHead>관심표명자 &nbsp;</CategoryHead>
        {total_rows}명
        </Typography>
        {/* 지원자 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <Icon_download_Gray style={{width:'20px', height:'20px', marginRight:'8px'}}/>
        <CategoryHead>지원자 &nbsp;</CategoryHead>
        {currentPromotion?.apply_cnt}명
        </Typography>
        {/* 작성자 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <PersonIcon fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <CategoryHead>작성자 &nbsp;</CategoryHead>
        {currentPromotion?.writer}
        </Typography>
        {/* 메모 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <AppImage name='IconSchool' customStyle={{marginRight: '8px', width:'20px',height:'20px'}}/>
        <CategoryHead>학교 특징 &nbsp;</CategoryHead>
        {currentPromotion?.school?.personal_memo}
        </Typography>
        {/* 홍보 결과 메모 */}
        <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <Icon_EdgeFoldPaper fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <CategoryHead>홍보 결과 메모 &nbsp;</CategoryHead>
        {currentPromotion?.content}
        </Typography>
    </div>
    <ThicDivider/>
    <PromoInterested currentPromotion={currentPromotion} setTab={setTab} hasAnyTag={hasAnyTag}/>
    </Container>
  )
}

export default PromoResultDetail